/**
 * LocalStorage keys for push device registration, keyed by wallet address.
 */

const DEVICE_ID_PREFIX = "avacus-device-id-"
const DEVICE_TOKEN_PREFIX = "avacus-device-token-"

export interface StoredPushDeviceRegistration {
  id: string | null
  token: string | null
}

function getStorageKey(
  prefix: string,
  address: string | undefined | null
): string {
  if (!address || typeof address !== "string") return prefix + "guest"
  return prefix + address.toLowerCase()
}

export function getDeviceIdStorageKey(address: string | undefined | null): string {
  return getStorageKey(DEVICE_ID_PREFIX, address)
}

export function getDeviceTokenStorageKey(
  address: string | undefined | null
): string {
  return getStorageKey(DEVICE_TOKEN_PREFIX, address)
}

export function getStoredDeviceId(address: string | undefined | null): string | null {
  if (typeof window === "undefined" || !window.localStorage) return null
  const raw = window.localStorage.getItem(getDeviceIdStorageKey(address))
  return raw ?? null
}

export function getStoredDeviceToken(
  address: string | undefined | null
): string | null {
  if (typeof window === "undefined" || !window.localStorage) return null
  const raw = window.localStorage.getItem(getDeviceTokenStorageKey(address))
  return raw ?? null
}

export function setStoredDeviceId(
  address: string | undefined | null,
  deviceId: string
): void {
  if (typeof window === "undefined" || !window.localStorage) return
  window.localStorage.setItem(getDeviceIdStorageKey(address), deviceId)
}

export function setStoredDeviceToken(
  address: string | undefined | null,
  token: string
): void {
  if (typeof window === "undefined" || !window.localStorage) return
  window.localStorage.setItem(getDeviceTokenStorageKey(address), token)
}

export function setStoredPushDeviceRegistration(
  address: string | undefined | null,
  registration: StoredPushDeviceRegistration
): void {
  if (registration.id) {
    setStoredDeviceId(address, registration.id)
  }
  if (registration.token) {
    setStoredDeviceToken(address, registration.token)
  }
}

export function getStoredPushDeviceRegistration(
  address: string | undefined | null
): StoredPushDeviceRegistration {
  return {
    id: getStoredDeviceId(address),
    token: getStoredDeviceToken(address),
  }
}

export function removeStoredDeviceId(address: string | undefined | null): void {
  if (typeof window === "undefined" || !window.localStorage) return
  window.localStorage.removeItem(getDeviceIdStorageKey(address))
}

export function removeStoredDeviceToken(
  address: string | undefined | null
): void {
  if (typeof window === "undefined" || !window.localStorage) return
  window.localStorage.removeItem(getDeviceTokenStorageKey(address))
}

export function clearStoredPushDeviceRegistration(
  address: string | undefined | null
): void {
  removeStoredDeviceId(address)
  removeStoredDeviceToken(address)
}
